import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { 
  CreditCard, Check, Zap, Building2, Users, 
  HardDrive, Crown, ArrowRight, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '@/contexts/I18nContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICON_FOR_INDEX = [Building2, Zap, Shield, Crown];

export default function TenantSubscription() {
  const { t } = useI18n();
  const { currency: orgCurrency, symbol: orgSymbol } = useCurrency();
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [processing, setProcessing] = useState(false);

  const fetchSubscription = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const [subRes, plansRes] = await Promise.all([
        axios.get(`${API}/analytics/tenant/subscription`, { headers: { Authorization: `Bearer ${token}` } }),
        // Live CMS plans (Launch / Growth / Scale / etc.) — same source the marketing /pricing page reads.
        axios.get(`${BACKEND_URL}/api/plans/public`).catch(() => ({ data: [] })),
      ]);
      setSubscription(subRes.data);
      const livePlans = Array.isArray(plansRes.data) ? plansRes.data : [];
      // Normalise to the shape the existing UI expects.
      const sorted = [...livePlans]
        .filter(p => p?.is_active !== false)
        .sort((a, b) => (a.sort_order ?? 99) - (b.sort_order ?? 99));
      // Heuristically mark the middle plan as popular if none flagged.
      const anyHi = sorted.some(p => p.highlighted);
      setPlans(sorted.map((p, i) => {
        // Prefer the price matching the tenant's org currency. Fall back to
        // whichever price the plan has if no exact match.
        const inrPrice = p.price_monthly_inr;
        const usdPrice = p.price_monthly ?? p.price_monthly_usd;
        let price, currency;
        if (orgCurrency === 'INR' && inrPrice != null) { price = inrPrice; currency = 'INR'; }
        else if (orgCurrency !== 'INR' && usdPrice != null) { price = usdPrice; currency = orgCurrency; }
        else if (inrPrice != null) { price = inrPrice; currency = 'INR'; }
        else { price = usdPrice ?? 0; currency = orgCurrency; }
        return {
          id: p.id || p.slug,
          name: p.name,
          price,
          currency,
          users: p.users === -1 ? 'Unlimited' : p.users,
          storage_gb: p.storage_gb === -1 ? 'Unlimited' : p.storage_gb,
          tagline: p.tagline || '',
          features: Array.isArray(p.features) ? p.features : [],
          icon: ICON_FOR_INDEX[i] || Building2,
          popular: anyHi ? !!p.highlighted : i === Math.floor(sorted.length / 2),
        };
      }));
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
      toast.error('Failed to fetch subscription');
    } finally {
      setLoading(false);
    }
  }, [orgCurrency]);

  const loadRazorpay = useCallback(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    fetchSubscription();
    loadRazorpay();
  }, [fetchSubscription, loadRazorpay]);

  const handleUpgrade = (plan) => {
    if (plan.id === subscription?.plan || plan.price === 0) return;
    setSelectedPlan(plan);
    setUpgradeDialogOpen(true);
  };

  const processStripePayment = async () => {
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const tenant = JSON.parse(localStorage.getItem('tenant') || '{}');
      
      const response = await axios.post(`${API}/payments/stripe/checkout`, {
        plan_id: selectedPlan.id,
        tenant_id: tenant.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error('Failed to initiate payment');
      setProcessing(false);
    }
  };

  const processRazorpayPayment = async () => {
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const tenant = JSON.parse(localStorage.getItem('tenant') || '{}');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      const response = await axios.post(`${API}/payments/razorpay/create-order`, {
        plan_id: selectedPlan.id,
        tenant_id: tenant.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const options = {
        key: response.data.key_id,
        amount: response.data.amount,
        currency: response.data.currency,
        name: 'Recruitizr',
        description: `${selectedPlan.name} Plan Subscription`,
        order_id: response.data.order_id,
        handler: async function(razorpayResponse) {
          // Verify payment
          try {
            await axios.post(`${API}/payments/razorpay/verify`, {
              order_id: razorpayResponse.razorpay_order_id,
              payment_id: razorpayResponse.razorpay_payment_id,
              signature: razorpayResponse.razorpay_signature
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            toast.success('Payment successful! Plan upgraded.');
            setUpgradeDialogOpen(false);
            fetchSubscription();
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: user.name,
          email: user.email
        },
        theme: {
          color: '#8b5cf6'
        }
      };
      
      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      toast.error('Failed to initiate Razorpay payment');
    } finally {
      setProcessing(false);
    }
  };

  const handlePayment = () => {
    if (paymentMethod === 'stripe') {
      processStripePayment();
    } else {
      processRazorpayPayment();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      {/* Current Plan */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-slate-900 flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-blue-600" />
            Current Subscription
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <Badge className="bg-blue-100 text-blue-700 text-lg px-4 py-1 capitalize">
                  {subscription?.plan || 'free'}
                </Badge>
                <Badge variant="outline" className={subscription?.status === 'active' ? 'text-emerald-600 border-emerald-300' : 'text-amber-600 border-amber-300'}>
                  {subscription?.status}
                </Badge>
              </div>
              {subscription?.expires_at && (
                <p className="text-slate-500 text-sm mt-2">
                  Expires: {new Date(subscription.expires_at).toLocaleDateString()}
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-500">Usage</p>
              <p className="text-lg font-bold text-slate-900">
                {subscription?.usage?.users} / {subscription?.limits?.users} users
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Available Plans */}
      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-4">Available Plans</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.length === 0 && !loading && (
            <div className="col-span-full text-center py-8 text-sm text-slate-500" data-testid="plans-empty">
              No plans available — please contact support.
            </div>
          )}
          {plans.map((plan) => {
            const Icon = plan.icon;
            const isCurrentPlan = subscription?.plan === plan.id;
            const priceLabel = (plan.currency === 'INR')
              ? `₹${Number(plan.price).toLocaleString('en-IN')}`
              : `${orgSymbol}${Number(plan.price).toLocaleString('en-US')}`;

            return (
              <Card
                key={plan.id}
                className={`relative ${
                  plan.popular ? 'border-blue-500 border-2' : 'border-slate-200'
                } ${isCurrentPlan ? 'bg-blue-50' : ''}`}
                data-testid={`plan-card-${plan.id}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-blue-600 text-white">Most Popular</Badge>
                  </div>
                )}
                <CardHeader className="text-center pb-2">
                  <div className={`mx-auto p-3 rounded-full ${isCurrentPlan ? 'bg-blue-600' : 'bg-slate-100'}`}>
                    <Icon className={`h-6 w-6 ${isCurrentPlan ? 'text-white' : 'text-slate-600'}`} />
                  </div>
                  <CardTitle className="text-slate-900" data-testid={`plan-name-${plan.id}`}>{plan.name}</CardTitle>
                  {plan.tagline && (
                    <p className="text-xs text-slate-500 mt-0.5">{plan.tagline}</p>
                  )}
                  <div className="mt-2">
                    <span className="text-3xl font-bold text-slate-900" data-testid={`plan-price-${plan.id}`}>{priceLabel}</span>
                    <span className="text-slate-500">/month</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 mb-4">
                    {(plan.users != null) && (
                      <li className="flex items-center gap-2 text-sm text-slate-600">
                        <Check className="h-4 w-4 text-emerald-500" />
                        {plan.users === 'Unlimited' ? 'Unlimited Users' : `${plan.users} Users`}
                      </li>
                    )}
                    {(plan.storage_gb != null) && (
                      <li className="flex items-center gap-2 text-sm text-slate-600">
                        <Check className="h-4 w-4 text-emerald-500" />
                        {plan.storage_gb === 'Unlimited' ? 'Unlimited Storage' : `${plan.storage_gb} GB Storage`}
                      </li>
                    )}
                    {plan.features.map((feature, idx) => (
                      <li key={`tena-${plan.id}-${idx}`} className="flex items-center gap-2 text-sm text-slate-600">
                        <Check className="h-4 w-4 text-emerald-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button
                    className={`w-full ${
                      isCurrentPlan
                        ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                        : plan.price === 0
                        ? 'bg-slate-100 text-slate-600'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                    disabled={isCurrentPlan || plan.price === 0}
                    onClick={() => handleUpgrade(plan)}
                    data-testid={`upgrade-${plan.id}`}
                  >
                    {isCurrentPlan ? 'Current Plan' : plan.price === 0 ? 'Free Forever' : 'Upgrade'}
                    {!isCurrentPlan && plan.price > 0 && <ArrowRight className="ml-2 h-4 w-4" />}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Upgrade Dialog */}
      <Dialog open={upgradeDialogOpen} onOpenChange={setUpgradeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upgrade to {selectedPlan?.name}</DialogTitle>
            <DialogDescription>
              Choose your preferred payment method
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-slate-600">Plan</span>
                <span className="font-bold text-slate-900">{selectedPlan?.name}</span>
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="text-slate-600">Price</span>
                <span className="font-bold text-slate-900">{selectedPlan?.currency === 'INR' ? '₹' : orgSymbol}{selectedPlan?.price}/month</span>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-700">Payment Method</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setPaymentMethod('stripe')}
                  className={`p-4 border rounded-lg text-center transition-colors ${
                    paymentMethod === 'stripe' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <CreditCard className="h-6 w-6 mx-auto text-blue-600" />
                  <p className="text-sm font-medium mt-2">Stripe</p>
                  <p className="text-xs text-slate-500">Cards, Wallets</p>
                </button>
                <button
                  onClick={() => setPaymentMethod('razorpay')}
                  className={`p-4 border rounded-lg text-center transition-colors ${
                    paymentMethod === 'razorpay' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <CreditCard className="h-6 w-6 mx-auto text-emerald-600" />
                  <p className="text-sm font-medium mt-2">Razorpay</p>
                  <p className="text-xs text-slate-500">UPI, Cards, Net Banking</p>
                </button>
              </div>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setUpgradeDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={handlePayment}
              disabled={processing}
            >
              {processing ? 'Processing...' : `Pay ${selectedPlan?.currency === 'INR' ? '₹' : orgSymbol}${selectedPlan?.price}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
